import { useRef, useState, type InputHTMLAttributes, type KeyboardEvent } from 'react'
import { CloseIcon, FolderIcon, SearchIcon, SortsIcon } from '@features/shared/icons/icons'
import { IconButton } from '@features/shared/components/IconButton'
import { DropdownMenu } from '@features/shared/components/DropdownMenu'
import type { SidebarTableSort } from '@features/settings/stores/useSettingsStore'
import { SIDEBAR_TABLE_SORT_OPTIONS } from '../logic/tableOrder'
import { MENU_HEADER_END_PAD_CLASS, MENU_ICON_SLOT_CLASS } from './menuIconSlots'

/** A full-width search row under the list's header, its icon in line with the rows' icons. */
export function TableListSearch({
  label,
  value,
  onChange,
  autoFocus = false,
  inputProps,
  startPaddingClassName = 'pl-[calc(var(--sidebar-item-inset)+0.5rem)]',
}: {
  label: string
  value: string
  onChange: (value: string) => void
  /** Takes focus as it appears (a search opened to type into). */
  autoFocus?: boolean
  /** A host outside the module menu can align search with its own rows. */
  startPaddingClassName?: string
  /**
   * More for the input (a list's keyboard: its own keydown runs first, and
   * Escape still clears the box unless it was handled).
   */
  inputProps?: Omit<InputHTMLAttributes<HTMLInputElement>, 'value' | 'onChange' | 'type'>
}) {
  const inputRef = useRef<HTMLInputElement | null>(null)

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    inputProps?.onKeyDown?.(event)
    if (event.defaultPrevented || event.key !== 'Escape' || !value) return
    event.preventDefault()
    event.stopPropagation()
    onChange('')
  }

  return (
    // A band across the menu, one rail row tall like the title band above it:
    // its icon and text where the rows' are (the list's inset and a row's
    // own), its clear button under the header's last icon. The line under it
    // is the top of the list and its scroll lane, blue while it has focus.
    <div
      className={`flex h-[var(--sidebar-row-height)] shrink-0 items-center gap-2 border-b border-[color:var(--sidebar-submenu-border)] ${startPaddingClassName} ${MENU_HEADER_END_PAD_CLASS} focus-within:border-[color:var(--border-info)]`}
    >
      <SearchIcon
        size={14}
        className="shrink-0 text-[color:var(--sidebar-submenu-text-faint)]"
        aria-hidden="true"
      />
      <input
        autoFocus={autoFocus}
        {...inputProps}
        ref={inputRef}
        type="search"
        value={value}
        onChange={(event) => onChange(event.target.value)}
        onKeyDown={handleKeyDown}
        placeholder={`Search ${label.toLocaleLowerCase()}`}
        aria-label={`Search ${label.toLocaleLowerCase()}`}
        className="min-w-0 flex-1 bg-transparent text-sm text-[color:var(--sidebar-submenu-text-default)] outline-none placeholder:text-[color:var(--sidebar-submenu-text-faint)] [&::-webkit-search-cancel-button]:hidden"
      />
      {value && (
        <IconButton
          label="Clear search"
          onClick={() => {
            onChange('')
            inputRef.current?.focus()
          }}
          className="flex h-6 w-6 items-center justify-center rounded text-[color:var(--sidebar-submenu-text-faint)] transition-colors hover:text-[color:var(--sidebar-submenu-text-default)]"
        >
          <CloseIcon size={12} aria-hidden="true" />
        </IconButton>
      )}
    </div>
  )
}

export function TableSortMenu({
  sort,
  onChange,
  label = 'Sort tables',
}: {
  sort: SidebarTableSort
  onChange: (sort: SidebarTableSort) => void
  /** The button's name: what the menu sorts. */
  label?: string
}) {
  const [open, setOpen] = useState(false)

  return (
    <DropdownMenu
      open={open}
      onOpenChange={setOpen}
      align="right"
      // A flex wrapper, so no line box sits under the button and it centers
      // with the header's other buttons.
      triggerClassName="flex"
      sections={[
        {
          label: 'Sort by',
          items: SIDEBAR_TABLE_SORT_OPTIONS.map((option) => ({
            type: 'option' as const,
            id: option.value,
            label: option.label,
            selected: option.value === sort,
            onSelect: () => onChange(option.value),
          })),
        },
      ]}
      trigger={
        <IconButton
          label={label}
          className={`${MENU_ICON_SLOT_CLASS} text-[color:var(--sidebar-submenu-text-faint)] transition-colors hover:text-[color:var(--sidebar-submenu-text-subtle)]`}
        >
          <SortsIcon size={15} aria-hidden="true" />
        </IconButton>
      }
    />
  )
}

export function TableFoldersToggle({
  foldersEnabled,
  onToggle,
}: {
  foldersEnabled: boolean
  onToggle: () => void
}) {
  return (
    <IconButton
      label={foldersEnabled ? 'Show flat table list' : 'Show folders'}
      onClick={onToggle}
      className={`${MENU_ICON_SLOT_CLASS} transition-colors ${
        // On, in yellow: the header's toggles each carry a color of their own
        // (folders yellow, favorites rose, hidden purple, the pin info blue).
        foldersEnabled
          ? 'tone-yellow text-[color:var(--tone-icon)] hover:text-[color:var(--tone-text)]'
          : 'text-[color:var(--sidebar-submenu-text-faint)] hover:text-[color:var(--sidebar-submenu-text-subtle)]'
      }`}
      aria-pressed={foldersEnabled}
    >
      <FolderIcon size={15} filled={foldersEnabled} aria-hidden="true" />
    </IconButton>
  )
}
